import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	input,
	OnChanges,
	OnInit,
	signal,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Routes } from '@sp-client/app/enums/routes.enum';
import { ConfigService } from '@sp-client/app/services/config.service';
import { SidePanelService } from '@sp-client/app/services/side-panel.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IconComponent } from '../icon/icon.component';

import { LogService } from '../../services/log.service';
@Component({
	selector: 'sp-side-panel',
	imports: [CommonModule, RouterOutlet, IconComponent],
	templateUrl: './side-panel.component.html',
	styleUrl: './side-panel.component.scss',
	animations: [
		trigger('panelSlide', [
			state('out', style({ transform: 'translate3d(100%, 0, 0)' })),
			state('in', style({ transform: 'translate3d(0, 0, 0)' })),
			transition('out <=> in', animate('200ms ease-in-out'))
		])
	]
})
export class SidePanelComponent implements OnInit, AfterViewInit, OnChanges {
	@ViewChild('sidePanelScroll') sidePanel!: ElementRef;
	@ViewChild(RouterOutlet) panelOutlet: RouterOutlet;
	private panelOutletSub: Subscription;

	// open = model(false);
	renderReady = signal(false);
	hasCloseBackground = input(true);
	hasSeeAllButton = false;
	currentUrl: string;

	constructor(
		private router: Router,
		private sidePanelService: SidePanelService,
		private cdRef: ChangeDetectorRef,
		private configService: ConfigService,
		private log: LogService
	) {}

	ngOnInit(): void {
		if (!this.configService.isBrowserSignal()) return;

		const validRoutes: string[] = [Routes.NeighborhoodGuide];

		this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			this.currentUrl = event.urlAfterRedirects;
			this.hasSeeAllButton = validRoutes.includes(this.currentUrl);
		});
	}

	ngAfterViewInit(): void {
		this.panelOutletSub = this.panelOutlet.activateEvents.subscribe(() => {
			this.sidePanelService.setIsReady(false);
			this.forcePanelContentChangeDetection();
		});
		if (!this.configService.isBrowserSignal()) return;

		requestAnimationFrame(() => {
			this.renderReady.set(true);
		});
		this.cdRef.markForCheck();
		this.cdRef.detectChanges();
	}

	ngOnChanges(_changes: SimpleChanges): void {
		this.log.debug('changes');
		if (this.panelOutlet && this.panelOutlet.isActivated) {
			const loadedComponent = this.panelOutlet.component as any;
			if (loadedComponent && loadedComponent.cdRef) {
				loadedComponent.cdRef.detectChanges();
			}
		}
	}

	ngOnDestroy(): void {
		this.panelOutletSub?.unsubscribe();
	}

	forcePanelContentChangeDetection(): void {
		requestAnimationFrame(() => {
			this.cdRef.detectChanges();

			if (this.panelOutlet?.isActivated) {
				const loadedComponent = this.panelOutlet.component as any;
				if (loadedComponent?.cdRef) {
					loadedComponent.cdRef.detectChanges();
				}
			}

			requestAnimationFrame(() => {
				if (this.sidePanelService.isOpenSignal()) {
					this.sidePanelService.setIsReady(true);
				}
			});
		});
	}

	getScrollTop(): number {
		return this.sidePanel.nativeElement.scrollTop;
	}

	setScrollTo(x: number, y: number): void {
		this.sidePanel.nativeElement.scrollTo(x, y);
	}

	setScrollTop(y: number): void {
		this.sidePanel.nativeElement.scrollTop = y;
	}

	get animationState(): string {
		if (!this.renderReady()) return 'out';
		return this.sidePanelService.isOpenSignal() ? 'in' : 'out';
	}

	onClosePanel() {
		this.sidePanelService.closePanel();
		this.router.navigate(['', { outlets: { panel: null } }]);
	}

	onSeeAllButton() {}

	onAnimationDone(event: any): void {
		if (event.toState === 'in') {
			if (this.panelOutlet && this.panelOutlet.isActivated) {
				const loadedComponent = this.panelOutlet.component as any;
				if (loadedComponent && loadedComponent.cdRef) {
					loadedComponent.cdRef.detectChanges();
				}
			}
			this.sidePanelService.setIsReady(true);
			return;
		}

		this.sidePanelService.setIsReady(false);
	}

	@HostListener('document:keydown.escape', ['$event'])
	onEscapeKey(_event: KeyboardEvent) {
		if (!this.sidePanelService.isOpenSignal()) return;
		this.onClosePanel();
	}
}
